import type { FC } from 'react';
import {
    Flex,
    Inline,
    Interactable,
    Link,
    ListItem,
    UnreadIndicator,
} from '@george-labs.com/design-system';

import { formatDate } from '@luna/date';
import { generatePath } from '@luna/router';
import { getCleanMessageBody } from '@shared/conversations-utils';

import type { BankMessage as BankMessageType } from '../BankMessagesTypes';
import { isBankMessageUnread } from '../BankMessagesService';
import { BankMessageIcon } from '../BankMessageIcon';
import { getBankMessageSubtitle } from './BankMessageService';

interface Props {
    message: BankMessageType;
    messageAbsolutePath: string;
    showSender: boolean;
    showIcon: boolean;
}

export const BankMessage: FC<Props> = ({
    showSender,
    showIcon,
    messageAbsolutePath,
    message: { id, from, date, subject, body, flags },
}) => {
    const { subtitle, subtitleDataCy } = getBankMessageSubtitle(showSender, from, subject);
    const isMessageUnread = isBankMessageUnread(flags);

    return (
        <ListItem className="p-0">
            <Interactable
                defaultAction={
                    <Link href={generatePath(messageAbsolutePath, { bankMessageId: id })} />
                }
                data-cy="bank-message"
                className="my-2"
            >
                <Flex align="center">
                    {showIcon && <BankMessageIcon flags={flags} />}
                    <div className="overflow-hidden w-100">
                        <Flex justify="space-between">
                            <Flex align="center">
                                {isMessageUnread && (
                                    <UnreadIndicator data-cy="unread-bank-message" />
                                )}
                                <Inline emphasis data-cy={subtitleDataCy} className="text-truncate">
                                    {subtitle}
                                </Inline>
                            </Flex>
                            <Inline small faded data-cy="receiving-date" className="text-nowrap">
                                {formatDate(date)}
                            </Inline>
                        </Flex>
                        <p className="text-truncate" data-cy="message-preview">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: getCleanMessageBody(body),
                                }}
                            />
                        </p>
                    </div>
                </Flex>
            </Interactable>
        </ListItem>
    );
};
